// web/src/pages/finance/ReconciledTodayPage.tsx
//
// I-5 — Reconciled-Today dashboard.
//
// Shows 4 summary tiles (payments, invoices issued, transactions, exceptions)
// then 3 side-by-side lists (recent payments, recent invoices, recent transactions).
// Auto-refreshes every 60 s via refetchInterval.
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router";
import { ChevronLeft } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  getReconciliationToday,
  type PaymentRecordSummaryDto,
  type InvoiceSummaryDto,
  type TransactionRowDto,
} from "@/lib/invoices-payments";
import { ReconciliationTile } from "@/components/reconciliation-tile";
import { PaymentMethodBadge } from "@/components/payment-method-badge";
import { InvoiceStatusBadge, type InvoiceStatusCode } from "@/components/invoice-status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/app/layouts/PageContainer";
import { formatDate } from "@/lib/format";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Currency formatter (dollars, not cents). */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount);
}

// ---------------------------------------------------------------------------
// Sub-components (inline to avoid exporting implementation details)
// ---------------------------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-base font-semibold text-foreground">{children}</h2>
  );
}

function EmptyRow({ label }: { label: string }) {
  return (
    <p className="py-4 text-center text-sm text-muted-foreground">{label}</p>
  );
}

interface RecentPaymentsListProps {
  rows: PaymentRecordSummaryDto[];
}

function RecentPaymentsList({ rows }: RecentPaymentsListProps) {
  const navigate = useNavigate();
  if (rows.length === 0) return <EmptyRow label="No payments today." />;

  return (
    <ul className="divide-y divide-border text-sm" role="list">
      {rows.map((row) => (
        <li
          key={row.paymentRecordId}
          className="flex cursor-pointer items-center justify-between gap-2 py-2 hover:bg-muted/50"
          onClick={() => navigate(`/payments/${row.paymentRecordId}`)}
          role="listitem"
          aria-label={`Payment ${row.paymentRecordId}`}
        >
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">
              #{row.paymentRecordId}
            </p>
            <p className="text-xs text-muted-foreground">
              {row.transactionDate ? formatDate(row.transactionDate) : "—"}
            </p>
          </div>
          <PaymentMethodBadge code={row.paymentTypeCode} />
          <span className="shrink-0 text-right font-mono text-xs">
            {formatCurrency(row.tranAmount)}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {row.status}
          </span>
        </li>
      ))}
    </ul>
  );
}

interface RecentInvoicesListProps {
  rows: InvoiceSummaryDto[];
}

function RecentInvoicesList({ rows }: RecentInvoicesListProps) {
  const navigate = useNavigate();
  if (rows.length === 0) return <EmptyRow label="No invoices issued today." />;

  return (
    <ul className="divide-y divide-border text-sm" role="list">
      {rows.map((row) => (
        <li
          key={row.invoiceId}
          className="flex cursor-pointer items-center justify-between gap-2 py-2 hover:bg-muted/50"
          onClick={() => navigate(`/invoices/${row.invoiceId}`)}
          role="listitem"
          aria-label={`Invoice ${row.invoiceReferenceNo}`}
        >
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{row.invoiceReferenceNo}</p>
            <p className="truncate text-xs text-muted-foreground">
              {row.clientFullName ?? "—"}
            </p>
          </div>
          <span className="shrink-0 font-mono text-xs">
            {formatCurrency(row.invoiceTotalIncGst)}
          </span>
          <InvoiceStatusBadge code={row.invoiceStatusCode as InvoiceStatusCode} />
        </li>
      ))}
    </ul>
  );
}

interface RecentTransactionsListProps {
  rows: TransactionRowDto[];
}

function RecentTransactionsList({ rows }: RecentTransactionsListProps) {
  const navigate = useNavigate();
  if (rows.length === 0) return <EmptyRow label="No transactions today." />;

  function handleClick(row: TransactionRowDto) {
    if (row.invoiceId != null) {
      navigate(`/invoices/${row.invoiceId}`);
    } else if (row.paymentRecordId != null) {
      navigate(`/payments/${row.paymentRecordId}`);
    }
    // else: no-op
  }

  return (
    <ul className="divide-y divide-border text-sm" role="list">
      {rows.map((row) => (
        <li
          key={row.tranId}
          className={[
            "flex items-center justify-between gap-2 py-2",
            row.invoiceId != null || row.paymentRecordId != null
              ? "cursor-pointer hover:bg-muted/50"
              : "",
          ].join(" ")}
          onClick={() => handleClick(row)}
          role="listitem"
          aria-label={`Transaction ${row.tranId}`}
        >
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">
              {row.accountReferenceNo} — {row.tranTypeCode}
            </p>
            <p className="text-xs text-muted-foreground">
              {row.transactionDate ? formatDate(row.transactionDate) : "—"}
              {row.invoiceReferenceNo && (
                <span className="ml-1">· inv {row.invoiceReferenceNo}</span>
              )}
              {row.paymentReferenceNo && (
                <span className="ml-1">· pmt {row.paymentReferenceNo}</span>
              )}
            </p>
          </div>
          <span className="shrink-0 font-mono text-xs">
            {formatCurrency(row.tranAmount)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Page skeleton (loading state)
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <PageContainer>
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Reconciled Today</h1>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// ReconciledTodayPage
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ReconciledTodayPageProps {}

export function ReconciledTodayPage(_props: ReconciledTodayPageProps = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ["reconciliation-today"],
    queryFn: getReconciliationToday,
    refetchInterval: 60_000,
  });

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
  }, []);

  if (isLoading || !data) {
    return <LoadingSkeleton />;
  }

  return (
    <PageContainer>
      {portalNode && createPortal(
        <Link to="/admin" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back
        </Link>,
        portalNode
      )}
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Reconciled Today</h1>

        {/* ------------------------------------------------------------------ */}
        {/* Summary tiles                                                        */}
        {/* ------------------------------------------------------------------ */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <ReconciliationTile
          title="Payments today"
          value={data.paymentsCount}
          subText={formatCurrency(data.paymentsTotal)}
        />
        <ReconciliationTile
          title="Invoices issued today"
          value={data.invoicesIssuedCount}
          subText={formatCurrency(data.invoicesIssuedTotal)}
        />
        <ReconciliationTile
          title="Transactions today"
          value={data.transactionsCount}
        />
        <ReconciliationTile
          title="Exceptions"
          value={data.failedPaymentPostingsCount}
          exceptionCount={
            data.failedPaymentPostingsCount > 0
              ? data.failedPaymentPostingsCount
              : undefined
          }
        />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Three side-by-side lists                                             */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent payments */}
        <section className="rounded-lg border bg-card p-4 shadow-sm">
          <SectionHeading>Recent payments</SectionHeading>
          <RecentPaymentsList rows={data.recentPayments} />
        </section>

        {/* Recent invoices issued */}
        <section className="rounded-lg border bg-card p-4 shadow-sm">
          <SectionHeading>Recent invoices issued</SectionHeading>
          <RecentInvoicesList rows={data.recentInvoicesIssued} />
        </section>

        {/* Recent transactions */}
        <section className="rounded-lg border bg-card p-4 shadow-sm">
          <SectionHeading>Recent transactions</SectionHeading>
          <RecentTransactionsList rows={data.recentTransactions} />
        </section>
      </div>
      </div>
    </PageContainer>
  );
}
